import { isNil, round } from 'lodash';
import { HomeServiceOrderPricingType } from '../../../../gql/graphql';
import { defaultHomeServiceGogoFee } from '../../../../constants/homeService';

const depositDuePercentage = 30;

export interface HomeServiceQuoteInput {
  homeServiceName?: string | null;
  pricingType?: HomeServiceOrderPricingType | null;
  hours?: number | string | null;
  providerRate?: number | string | null;
  gogoFee?: number | string | null;
  findersFee?: number | string | null;
  membershipFee?: number | string | null;
  estimatedDurationHours?: number | string | null;
}

export const calculateQuoteTotal = (quote: HomeServiceQuoteInput): number | null => {
  const providerRate = Number(quote.providerRate) || 0;
  const gogoFee = Number(quote.gogoFee) || 0;
  const findersFee = Number(quote.findersFee) || 0;
  const membershipFee = Number(quote.membershipFee) || 0;

  if (quote.pricingType === HomeServiceOrderPricingType.Fixed) {
    return providerRate + gogoFee + findersFee + membershipFee;
  }

  if (quote.pricingType === HomeServiceOrderPricingType.Hourly) {
    return (Number(quote.hours) || 0) * (providerRate + gogoFee) + findersFee + membershipFee;
  }

  return null;
};

export const calculateDepositDue = (quote: HomeServiceQuoteInput): number | null => {
  if (!quote.pricingType) {
    return null;
  }

  const providerRate = Number(quote.providerRate) || 0;
  const serviceCost = quote.pricingType === HomeServiceOrderPricingType.Fixed ? providerRate : (Number(quote.hours) || 0) * providerRate;

  return round((serviceCost * depositDuePercentage) / 100, 2);
};

export const calculateSuggestedGogoFee = (quote: HomeServiceQuoteInput): number | null => {
  if (quote.pricingType === HomeServiceOrderPricingType.Hourly) {
    return defaultHomeServiceGogoFee;
  }

  if (quote.pricingType !== HomeServiceOrderPricingType.Fixed || !quote.providerRate && quote.providerRate !== 0) {
    return null;
  }

  const providerRate = Number(quote.providerRate);
  return round((providerRate * (providerRate <= 200 ? 10 : 5)) / 100, 2);
};

export const isQuoteComplete = (quote: HomeServiceQuoteInput): boolean => Boolean(
  quote.homeServiceName?.trim() &&
    quote.pricingType &&
    Number(quote.hours) > 0 &&
    !isNil(quote.providerRate) &&
    !isNil(quote.gogoFee) &&
    (quote.pricingType !== HomeServiceOrderPricingType.Fixed || Number(quote.hours) === 1),
);

export const formatMoney = (amount: number | string | null | undefined): string =>
  amount || amount === 0 ? `$${Number(amount).toFixed(2)}` : '—';

export const buildQuotePayload = (quote: HomeServiceQuoteInput) => {
  const hours = quote.pricingType === HomeServiceOrderPricingType.Fixed ? 1 : quote.hours;

  return {
    hours: hours || hours === 0 ? Number(hours) : null,
    providerRate: quote.providerRate || quote.providerRate === 0 ? Number(quote.providerRate) : null,
    gogoFee: quote.gogoFee || quote.gogoFee === 0 ? Number(quote.gogoFee) : undefined,
    findersFee: quote.findersFee || quote.findersFee === 0 ? Number(quote.findersFee) : null,
    membershipFee: quote.membershipFee || quote.membershipFee === 0 ? Number(quote.membershipFee) : null,
    estimatedDurationHours:
      quote.pricingType === HomeServiceOrderPricingType.Fixed && (quote.estimatedDurationHours || quote.estimatedDurationHours === 0)
        ? Number(quote.estimatedDurationHours)
        : null,
  };
};
